import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';

export type DateRangePreset = 'MTD' | 'QTD' | 'YTD' | 'L30D' | 'L90D' | 'CUSTOM';

interface DateRange {
  startDate: Date;
  endDate: Date;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
}

function getPresetDates(preset: DateRangePreset): DateRange {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  switch (preset) {
    case 'MTD':
      return {
        startDate: new Date(year, month, 1),
        endDate: now,
      };
    case 'QTD': {
      const quarterStartMonth = Math.floor(month / 3) * 3;
      return {
        startDate: new Date(year, quarterStartMonth, 1),
        endDate: now,
      };
    }
    case 'YTD':
      return {
        startDate: new Date(year, 0, 1),
        endDate: now,
      };
    case 'L30D':
      return {
        startDate: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
        endDate: now,
      };
    case 'L90D':
      return {
        startDate: new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000),
        endDate: now,
      };
    default:
      return {
        startDate: new Date(year, month, 1),
        endDate: now,
      };
  }
}

function formatDateRange(range: DateRange): string {
  const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
  const start = range.startDate.toLocaleDateString('en-GB', options);
  const end = range.endDate.toLocaleDateString('en-GB', options);
  return `${start} - ${end}`;
}

export function DateRangePicker({ value, onChange, className }: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const presets: { label: string; value: DateRangePreset }[] = [
    { label: 'Month to Date', value: 'MTD' },
    { label: 'Quarter to Date', value: 'QTD' },
    { label: 'Year to Date', value: 'YTD' },
    { label: 'Last 30 Days', value: 'L30D' },
    { label: 'Last 90 Days', value: 'L90D' },
  ];

  function handlePresetClick(preset: DateRangePreset) {
    onChange(getPresetDates(preset));
    setIsOpen(false);
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'max-w-full min-w-0 justify-start text-left font-normal sm:w-auto',
            className
          )}
        >
          <Calendar className="mr-2 h-4 w-4 shrink-0" />
          <span className="truncate">{formatDateRange(value)}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="end">
        <div className="flex flex-col gap-1">
          {presets.map((preset) => (
            <Button
              key={preset.value}
              variant="ghost"
              className="justify-start"
              onClick={() => handlePresetClick(preset.value)}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function useDateRange(initialPreset: DateRangePreset = 'MTD') {
  const [dateRange, setDateRange] = useState<DateRange>(() => getPresetDates(initialPreset));
  return { dateRange, setDateRange };
}

export default DateRangePicker;
